import { MenuItem, Paper } from '@mui/material';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useMutation } from 'react-query';
import { useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';
import * as Yup from 'yup';
import {
  FormikSelect,
  FormikTextField,
  MaterialAutocomplete,
} from '../../../common/formik-material-ui';
import { user } from '../../../recoil/atoms/user';
import { makePHPRequest } from '../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';
import { ModalWithStyles } from './ModalWithStyles';

export function EditNumberModal({
  show,
  handleClose,
  refetchBusinessNumbers,
  methodNameEnum,
  number,
  enqueueSnackbarWithStyles,
  businessSMSRulesLoadable,
}) {
  const editNumberSchema = useMemo(
    () =>
      Yup.object({
        numberName: Yup.string().max(
          100,
          'Name must be a maximum of 100 characters'
        ),
      }),
    []
  );
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  const mutation = useMutation(getApiMutation(userAtom.contents, setUserAtom));
  const businessSmsEnabled = process.env.VITE_APP_BUSINESS_SMS === '1';
  const isExternalDirectoryNumber = useMemo(
    () => number?.portStatus === 'externalDirectory',
    [number]
  );
  const data = {
    call: methodNameEnum.GET_BUSINESS_CONFERENCES,
  };
  const businessConferences = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_CONFERENCES,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const conferences = useMemo(
    () =>
      businessConferences.state === 'hasValue'
        ? [
            ...(businessConferences.contents?.response?.data?.result ?? []),
          ]?.sort((a, b) =>
            a?.name?.toLowerCase()?.localeCompare(b?.name?.toLowerCase())
          )
        : [],
    [businessConferences]
  );
  const dataUsers = {
    call: methodNameEnum.GET_BUSINESS_USERS,
  };
  const businessUsers = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify(dataUsers),
    })
  );
  const activeUsers = useMemo(
    () =>
      businessUsers.state === 'hasValue'
        ? businessUsers.contents?.response?.data?.result
            ?.filter((user) => user.isactive === 1)
            ?.sort((a, b) =>
              a.fname.toLowerCase().localeCompare(b.fname.toLowerCase())
            )
        : [],
    [businessUsers]
  );
  const dataRingGroups = {
    call: methodNameEnum.GET_BUSINESS_GROUPS,
  };
  const ringGroups = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_GROUPS,
      payloadJsonString: JSON.stringify(dataRingGroups),
    })
  );
  const groups = useMemo(
    () =>
      ringGroups.state === 'hasValue'
        ? [
            ...(ringGroups.contents?.response?.data?.result ?? []),
          ]?.sort((a, b) =>
            a.groupname.toLowerCase().localeCompare(b.groupname.toLowerCase())
          )
        : [],
    [ringGroups]
  );
  const businessIvrs = useRecoilValueLoadable(
    makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_IVRS })
  );

  const ivrs = useMemo(
    () =>
      businessIvrs.state === 'hasValue'
        ? [...(businessIvrs.contents?.data?.result ?? [])]?.sort((a, b) =>
            a.IVRname.toLowerCase().localeCompare(b.IVRname.toLowerCase())
          )
        : [],
    [businessIvrs]
  );
  const businessQueues = useRecoilValueLoadable(
    makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_QUEUES })
  );
  const queues = useMemo(
    () =>
      businessQueues.state === 'hasValue'
        ? [...(businessQueues.contents?.data?.result ?? [])]?.sort((a, b) =>
            a.queuename.toLowerCase().localeCompare(b.queuename.toLowerCase())
          )
        : [],
    [businessQueues]
  );

  const numberForOptions = [
    'unallocated',
    'Agent',
    'Boost Set',
    'Conference',
    'Group',
    'IVR',
    'Queue',
  ];
  const typeLookup = {
    // unallocated is not working on production
    unallocated: {
      destinationid: '',
      type: 'unallocated',
    },
    Agent: {
      destinationid: 'agent',
      type: 'user',
    },
    Conference: {
      destinationid: 'conference',
      type: 'conference',
    },
    Queue: {
      destinationid: 'queue',
      type: 'queue',
    },
    Group: {
      destinationid: 'group',
      type: 'group',
    },
    IVR: {
      destinationid: 'ivr',
      type: 'ivr',
    },
    'Boost Set': {
      destinationid: '',
      type: 'localpresence',
    },
  };

  const businessSMSOptions = useMemo(
    () =>
      businessSMSRulesLoadable.state === 'hasValue' &&
      businessSMSRulesLoadable?.contents?.response?.data?.result?.length
        ? [
            { businessSMSId: '', name: 'None' },
            ...(businessSMSRulesLoadable?.contents?.response?.data?.result ??
              []),
          ]
        : [],
    [businessSMSRulesLoadable]
  );
  // React.useEffect(() => {
  //   setRefetchToggleForSMSRules({});
  // }, []);
  return (
    <Paper elevation={0} square={true}>
      <Formik
        onSubmit={async (values, actions) => {
          if (values?.numberFor?.toLowerCase() === 'agent') {
            const foundUser = activeUsers?.find(
              (user) => String(user?.userid) === String(values?.agent)
            );
            if (foundUser?.userTier?.toLowerCase() === 'payperuse') {
              enqueueSnackbarWithStyles({
                message: `Numbers cannot be assigned to “free” user. Please upgrade user access to continue`,
                alertProps: { severity: 'warning' },
              });
              return;
            }
          }
          const type = typeLookup[values.numberFor].type;
          const destinationid = typeLookup[values.numberFor].destinationid
            ? values[typeLookup[values.numberFor].destinationid]
            : '';
          let data = {
            call: businessSmsEnabled
              ? methodNameEnum.EDIT_NUMBER2
              : methodNameEnum.EDIT_NUMBER,
            apikey: userAtom.contents.apikey,
            businessid: userAtom.contents.businessid,
            destinationid,
            type,
            defaultuser: values.defaultUser || 'none',
            numberid: number.Numberid,
            numberName: values.numberName,
          };
          if (businessSmsEnabled) {
            data = {
              ...data,
              businessSMSId:
                type === 'user' || values.businessSMSId === ''
                  ? null
                  : values.businessSMSId,
            };
          }
          const response = await mutation.mutateAsync({
            endpoint: businessSmsEnabled
              ? endpointEnum.EDIT_NUMBER2
              : endpointEnum.EDIT_NUMBER,
            payload: data,
          });

          if (response?.data?.success) {
            enqueueSnackbarWithStyles({
              message: `Successfully Edited Number - ${number.DID}`,
              alertProps: { severity: 'success' },
            });
          } else {
            enqueueSnackbarWithStyles({
              message: `Encountered an error trying to edit the ${number.DID} number`,
              alertProps: { severity: 'warning' },
            });
          }
          refetchBusinessNumbers();
          actions.resetForm({});
          handleClose();
        }}
        validationSchema={editNumberSchema}
        enableReinitialize={true}
        initialValues={{
          numberFor:
            number.type === 'Directory' ? 'unallocated' : number.type ?? '',
          agent: number.destinationid ?? '',
          conference: number.conferenceid ?? '',
          group: number.groupid ?? '',
          ivr: number.ivrid ?? '',
          queue: number.queueid ?? '',
          defaultUser: number.defaultuser ?? '',
          numberName: number.numberName ?? '',
          businessSMSId: number.businessSMSId ?? '',
        }}
      >
        {(formik) => {
          const defaultUser =
            activeUsers?.find(
              (user) => user.userid === formik.values.defaultUser
            ) || null;

          const defaultConference =
            conferences?.find(
              (conf) => conf.conferenceid === Number(formik.values.conference)
            ) || null;

          const defaultGroup =
            groups?.find(
              (group) => group.maingroupid === Number(formik.values.group)
            ) || null;

          const defaultUser2 =
            activeUsers?.find(
              (user) => Number(user.userid) === Number(formik.values.agent)
            ) || null;

          const defaultIvr =
            ivrs?.find((ivr) => Number(ivr.mainIVRid) === formik.values.ivr) ||
            null;

          const defaultQueue =
            queues?.find((q) => +q.queueid === formik.values.queue) || null;
          const editNumber = number.DID ? number.DID : number.number;
          return (
            <ModalWithStyles
              onSubmit={formik.handleSubmit}
              title={`Edit Number - ${editNumber}`}
              aria-labelledby="example-modal-sizes-title-lg"
              show={show}
              onHide={() => {
                handleClose();
                formik.resetForm({});
              }}
              isSubmitting={formik.isSubmitting}
              isLoading={formik.isSubmitting}
              size="md"
            >
              <div className="container mt-3">
                <div className="row borderBottom">
                  <div className="col-lg-8">
                    <h5 className="fieldTitle">Number For</h5>
                    <p className="fieldDescription">
                      Choose from the dropdown to designate this number for a
                      specific Kixie entity like Agent, Queue, or others.
                      Calling this number will route the call to the selected
                      entity.
                    </p>
                  </div>
                  <div className="col-lg-4">
                    <FormikSelect
                      id="standard-basic"
                      label="Number For"
                      name="numberFor"
                      formik={formik}
                      margin="dense"
                      size="small"
                      variant="outlined"
                      disabled={isExternalDirectoryNumber}
                      fullWidth
                    >
                      {numberForOptions.map((option, index) => (
                        <MenuItem key={index} value={option}>
                          {isExternalDirectoryNumber ? 'Directory' : option}
                        </MenuItem>
                      ))}
                    </FormikSelect>
                  </div>
                </div>
                <div className="row borderBottom">
                  <div className="col-lg-8">
                    <h5 className="fieldTitle">Default User</h5>
                    <p className="fieldDescription">
                      (Optional) Assign a default user to this number. The CRM
                      connection of the selected agent will be used to pull
                      contact information for calls and log missed calls in the
                      CRM.
                    </p>
                  </div>
                  <div className="col-lg-4">
                    <MaterialAutocomplete
                      type="text"
                      fullWidth
                      label="Default User"
                      margin="dense"
                      size="small"
                      variant="outlined"
                      multiple={false}
                      options={activeUsers}
                      value={defaultUser}
                      disabled={isExternalDirectoryNumber}
                      onChange={(event, newValue) =>
                        formik.setFieldValue(
                          'defaultUser',
                          newValue ? newValue.userid : ''
                        )
                      }
                      getOptionLabel={(option) =>
                        option?.fname ? `${option?.fname} ${option?.lname}` : ''
                      } // collapse to empty string if no option found
                      isOptionEqualToValue={(option, value) =>
                        option?.userid === value?.userid
                      }
                      renderOption={(props, option) => {
                        const optionProps = { ...props, key: option.userid };
                        return (
                          <li {...optionProps}>
                            {`${option?.fname} ${option?.lname}`}
                          </li>
                        );
                      }}
                    />
                  </div>
                </div>
                {formik.values.numberFor === 'Agent' && (
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Select Agent</h5>
                      <p className="fieldDescription">
                        Choose a specific agent from the dropdown to apply this
                        number to the selection.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <MaterialAutocomplete
                        type="text"
                        fullWidth
                        label="Select Agent"
                        margin="dense"
                        size="small"
                        variant="outlined"
                        multiple={false}
                        options={activeUsers}
                        required
                        value={defaultUser2}
                        onChange={(event, newValue) =>
                          formik.setFieldValue(
                            'agent',
                            newValue ? newValue.userid : ''
                          )
                        }
                        getOptionLabel={(option) =>
                          option?.fname ? `${option?.fname} ${option?.lname}` : ''
                        } // collapse to empty string if no option found
                        isOptionEqualToValue={(option, value) =>
                          option?.userid === value?.userid
                        }
                        renderOption={(props, option) => {
                          const optionProps = { ...props, key: option.userid };
                          return (
                            <li
                              {...optionProps}
                              data-qa="default-action-user-autocomplete-item"
                            >
                              {`${option?.fname} ${option?.lname}`}
                            </li>
                          );
                        }}
                      />
                    </div>
                  </div>
                )}
                {formik.values.numberFor === 'Conference' && (
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Select Conference</h5>
                      <p className="fieldDescription">
                        Choose a specific conference from the dropdown to apply
                        this number to the selection.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <MaterialAutocomplete
                        type="text"
                        fullWidth
                        label="Select Conference"
                        margin="dense"
                        size="small"
                        variant="outlined"
                        multiple={false}
                        options={conferences}
                        value={defaultConference}
                        onChange={(event, newValue) =>
                          formik.setFieldValue(
                            'conference',
                            newValue ? newValue.conferenceid : ''
                          )
                        }
                        getOptionLabel={(option) =>
                          option?.name ? `${option.name}` : ''
                        } // collapse to empty string if no option found
                        isOptionEqualToValue={(option, value) =>
                          option?.conferenceid === value?.conferenceid
                        }
                      />
                    </div>
                  </div>
                )}
                {formik.values.numberFor === 'Group' && (
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Select Group</h5>
                      <p className="fieldDescription">
                        Choose a specific group from the dropdown to apply this
                        number to the selection.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <MaterialAutocomplete
                        type="text"
                        fullWidth
                        label="Select Group"
                        name="group"
                        value={defaultGroup}
                        margin="dense"
                        size="small"
                        variant="outlined"
                        multiple={false}
                        options={groups}
                        onChange={(event, newValue) =>
                          formik.setFieldValue(
                            'group',
                            newValue ? newValue.maingroupid : ''
                          )
                        }
                        getOptionLabel={(option) =>
                          option?.groupname ? `${option.groupname}` : ''
                        } // collapse to empty string if no option found
                        isOptionEqualToValue={(option, value) =>
                          option?.maingroupid === value?.maingroupid
                        }
                      />
                    </div>
                  </div>
                )}
                {formik.values.numberFor === 'IVR' && (
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Select IVR</h5>
                      <p className="fieldDescription">
                        Choose a specific IVR from the dropdown to apply this
                        number to the selection.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <MaterialAutocomplete
                        type="text"
                        fullWidth
                        label="Select IVR"
                        margin="dense"
                        size="small"
                        variant="outlined"
                        multiple={false}
                        options={ivrs}
                        value={defaultIvr}
                        onChange={(event, newValue) =>
                          formik.setFieldValue(
                            'ivr',
                            newValue ? +newValue.mainIVRid : ''
                          )
                        }
                        getOptionLabel={(option) =>
                          option?.IVRname ? `${option.IVRname}` : ''
                        } // collapse to empty string if no option found
                        isOptionEqualToValue={(option, value) =>
                          +option?.mainIVRid === value?.mainIVRid
                        }
                      />
                    </div>
                  </div>
                )}
                {formik.values.numberFor === 'Queue' && (
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Select Queue</h5>
                      <p className="fieldDescription">
                        Choose a specific queue from the dropdown to apply this
                        number to the selection.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <MaterialAutocomplete
                        type="text"
                        fullWidth
                        label="Select Queue"
                        margin="dense"
                        size="small"
                        variant="outlined"
                        multiple={false}
                        options={queues}
                        value={defaultQueue}
                        onChange={(event, newValue) =>
                          formik.setFieldValue(
                            'queue',
                            newValue ? +newValue.queueid : ''
                          )
                        }
                        getOptionLabel={(option) =>
                          option?.queuename ? `${option.queuename}` : ''
                        } // collapse to empty string if no option found
                        isOptionEqualToValue={(option, value) =>
                          +option?.queueid === value?.queueid
                        }
                      />
                    </div>
                  </div>
                )}
                <div className="row borderBottom">
                  <div className="col-lg-8">
                    <h5 className="fieldTitle">Name</h5>
                    <p className="fieldDescription">
                      Create a name for the number to easily identify it on the
                      Numbers page and when viewing call reports.
                    </p>
                  </div>
                  <div className="col-lg-4">
                    <FormikTextField
                      id="standard-basic"
                      label="Name"
                      name="numberName"
                      formik={formik}
                      margin="dense"
                      size="small"
                      variant="outlined"
                      fullWidth
                    />
                  </div>
                </div>
                {number.sms !== 0 &&
                  businessSmsEnabled &&
                  formik.values.numberFor !== 'Agent' &&
                  businessSMSRulesLoadable.state === 'hasValue' &&
                  businessSMSOptions.length > 0 && (
                    <div className="row">
                      <div className="col-lg-8">
                        <h5 className="fieldTitle">Select Team SMS Rule</h5>
                        <p className="fieldDescription">
                          (Optional) Choose a Team SMS Rule to determine how
                          inbound SMS messages are handled and which agents
                          receive them as part of the Team SMS.
                        </p>
                      </div>
                      <div className="col-lg-4">
                        <FormikSelect
                          style={{ marginTop: '3px' }}
                          id="standard-basic"
                          label="Select Team SMS Rule"
                          onChange={(event, newValue) =>
                            formik.setFieldValue(
                              'businessSMSId',
                              newValue ?? ''
                            )
                          }
                          formik={formik}
                          margin="dense"
                          size="small"
                          variant="outlined"
                          fullWidth
                          name="businessSMSId"
                        >
                          {businessSMSOptions.map((rule, index) => (
                            <MenuItem key={index} value={rule.businessSMSId}>
                              {rule.name}
                            </MenuItem>
                          ))}
                        </FormikSelect>
                      </div>
                    </div>
                  )}
              </div>
            </ModalWithStyles>
          );
        }}
      </Formik>
    </Paper>
  );
}
